import React, { useState, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Check,
  X,
  Pencil,
  Trash2,
  Plus,
  Upload,
  AlertTriangle,
  Users,
  Trophy,
  Swords,
} from 'lucide-react';

const SHEET_META = {
  members: { label: 'Membri', icon: Users, color: 'bg-blue-500' },
  stats: { label: 'Classifica', icon: Trophy, color: 'bg-amber-500' },
  matches: { label: 'Partite', icon: Swords, color: 'bg-green-500' },
};

// Colonne da mostrare per ogni tipo di foglio (ordinate)
const VISIBLE_COLUMNS = {
  members: ['name', 'email', 'role', 'status', 'phone'],
  stats: ['name', 'email', 'matches_played', 'wins', 'draws', 'losses', 'total_points', 'goals_scored', 'goals_conceded'],
  matches: ['title', 'date_time', 'location_name', 'team_a', 'team_b', 'team_a_score', 'team_b_score', 'status', 'participants_team_a', 'participants_team_b'],
};

const COLUMN_LABELS = {
  name: 'Nome',
  email: 'Email',
  role: 'Ruolo',
  status: 'Stato',
  phone: 'Telefono',
  matches_played: 'Partite',
  wins: 'Vinte',
  draws: 'Pareggi',
  losses: 'Perse',
  total_points: 'Punti',
  goals_scored: 'Gol fatti',
  goals_conceded: 'Gol subiti',
  title: 'Titolo',
  date_time: 'Data/Ora',
  location_name: 'Luogo',
  team_a: 'Squadra A',
  team_b: 'Squadra B',
  team_a_score: 'Gol A',
  team_b_score: 'Gol B',
  participants_team_a: 'Giocatori A',
  participants_team_b: 'Giocatori B',
};

function EditableCell({ value, onChange, onCancel, onConfirm }) {
  const [editValue, setEditValue] = useState(value ?? '');

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') onConfirm(editValue);
    if (e.key === 'Escape') onCancel();
  };

  return (
    <div className="flex items-center gap-1">
      <Input
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
        className="h-7 text-xs min-w-[80px]"
        autoFocus
      />
      <button
        onClick={() => onConfirm(editValue)}
        className="text-green-600 hover:text-green-800 shrink-0"
      >
        <Check className="h-3.5 w-3.5" />
      </button>
      <button
        onClick={onCancel}
        className="text-red-500 hover:text-red-700 shrink-0"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function SheetTable({ sheetKey, data, onUpdateData }) {
  const [editingCell, setEditingCell] = useState(null); // { rowIdx, col }

  const columns = VISIBLE_COLUMNS[sheetKey] || data.mapped_columns.filter((c) => c !== '_row');
  const rows = data.rows;

  const handleCellClick = (rowIdx, col) => {
    setEditingCell({ rowIdx, col });
  };

  const handleConfirm = (rowIdx, col, newValue) => {
    const updatedRows = rows.map((row, idx) => {
      if (idx !== rowIdx) return row;
      return { ...row, [col]: newValue === '' ? null : newValue };
    });
    onUpdateData({ ...data, rows: updatedRows, count: updatedRows.length });
    setEditingCell(null);
  };

  const handleDeleteRow = (rowIdx) => {
    const updatedRows = rows.filter((_, idx) => idx !== rowIdx);
    onUpdateData({ ...data, rows: updatedRows, count: updatedRows.length });
  };

  const handleAddRow = () => {
    const emptyRow = { _row: rows.length + 2 };
    columns.forEach((col) => {
      emptyRow[col] = null;
    });
    const updatedRows = [...rows, emptyRow];
    onUpdateData({ ...data, rows: updatedRows, count: updatedRows.length });
    // Auto-edit first cell of new row
    setEditingCell({ rowIdx: updatedRows.length - 1, col: columns[0] });
  };

  const formatCellValue = (value) => {
    if (value === null || value === undefined) return '';
    if (Array.isArray(value)) return value.join('; ');
    return String(value);
  };

  const hasWarning = (row, col) => {
    if (sheetKey === 'members' && col === 'name' && !row.name && !row.email) return true;
    if (sheetKey === 'members' && col === 'email' && row.email && !row.email.includes('@')) return true;
    return false;
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {rows.length} {rows.length === 1 ? 'riga' : 'righe'} — clicca su una cella per modificarla
        </p>
        <Button variant="outline" size="sm" onClick={handleAddRow}>
          <Plus className="h-3.5 w-3.5 mr-1" />
          Aggiungi riga
        </Button>
      </div>

      <ScrollArea className="rounded-md border max-h-[420px]">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="w-8 text-center text-xs">#</TableHead>
              {columns.map((col) => (
                <TableHead key={col} className="text-xs whitespace-nowrap">
                  {COLUMN_LABELS[col] || col}
                </TableHead>
              ))}
              <TableHead className="w-8" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + 2} className="text-center text-muted-foreground py-8">
                  Nessuna riga trovata
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, rowIdx) => (
                <TableRow key={rowIdx} className="group">
                  <TableCell className="text-center text-xs text-muted-foreground">
                    {row._row || rowIdx + 2}
                  </TableCell>
                  {columns.map((col) => {
                    const isEditing =
                      editingCell?.rowIdx === rowIdx && editingCell?.col === col;
                    const cellValue = formatCellValue(row[col]);
                    const warn = hasWarning(row, col);

                    return (
                      <TableCell
                        key={col}
                        className={`text-xs py-1 px-2 cursor-pointer hover:bg-muted/70 transition-colors ${
                          warn ? 'bg-orange-50 dark:bg-orange-950/20' : ''
                        }`}
                        onClick={() => !isEditing && handleCellClick(rowIdx, col)}
                      >
                        {isEditing ? (
                          <EditableCell
                            value={cellValue}
                            onConfirm={(v) => handleConfirm(rowIdx, col, v)}
                            onCancel={() => setEditingCell(null)}
                          />
                        ) : (
                          <div className="flex items-center gap-1 min-h-[24px]">
                            {warn && (
                              <AlertTriangle className="h-3 w-3 text-orange-500 shrink-0" />
                            )}
                            <span className={cellValue ? '' : 'text-muted-foreground italic'}>
                              {cellValue || '—'}
                            </span>
                            <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-40 ml-auto shrink-0" />
                          </div>
                        )}
                      </TableCell>
                    );
                  })}
                  <TableCell className="p-0">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteRow(rowIdx);
                      }}
                      className="p-1 text-muted-foreground hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Elimina riga"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </ScrollArea>
    </div>
  );
}

export function ImportPreviewTable({ previewData, onConfirm, onCancel, confirming }) {
  const [data, setData] = useState(previewData);
  const sheetsFound = data.sheets_found || [];
  const [activeTab, setActiveTab] = useState(sheetsFound[0] || 'members');

  const handleUpdateSheet = useCallback(
    (sheetKey, sheetData) => {
      setData((prev) => ({
        ...prev,
        preview: { ...prev.preview, [sheetKey]: sheetData },
      }));
    },
    []
  );

  const totalRows = sheetsFound.reduce(
    (sum, key) => sum + (data.preview?.[key]?.count || 0),
    0
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-lg">Anteprima import</h3>
          <Badge variant="secondary">{totalRows} righe totali</Badge>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel} disabled={confirming}>
            <X className="h-4 w-4 mr-1" />
            Annulla
          </Button>
          <Button onClick={() => onConfirm(data)} disabled={confirming || totalRows === 0}>
            <Upload className="h-4 w-4 mr-1" />
            {confirming ? 'Importazione...' : `Conferma import (${totalRows} righe)`}
          </Button>
        </div>
      </div>

      {sheetsFound.length === 0 ? (
        <Alert variant="destructive">
          <AlertDescription>Nessun foglio riconosciuto nel file.</AlertDescription>
        </Alert>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            {sheetsFound.map((key) => {
              const meta = SHEET_META[key] || { label: key, icon: Users };
              const Icon = meta.icon;
              const count = data.preview?.[key]?.count || 0;
              return (
                <TabsTrigger key={key} value={key} className="gap-1.5">
                  <Icon className="h-4 w-4" />
                  {meta.label}
                  <Badge variant="outline" className="ml-1 text-xs h-5 px-1.5">
                    {count}
                  </Badge>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {sheetsFound.map((key) => (
            <TabsContent key={key} value={key} className="mt-3">
              {data.preview?.[key] ? (
                <SheetTable
                  sheetKey={key}
                  data={data.preview[key]}
                  onUpdateData={(updated) => handleUpdateSheet(key, updated)}
                />
              ) : (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  Nessun dato per questo foglio.
                </p>
              )}
            </TabsContent>
          ))}
        </Tabs>
      )}

      <div className="text-xs text-muted-foreground space-y-1 border-t pt-3">
        <p><strong>Legenda colonne:</strong> I nomi delle colonne sono stati normalizzati automaticamente dal file originale.</p>
        <p>Le celle con <AlertTriangle className="h-3 w-3 text-orange-500 inline" /> indicano possibili problemi (nome/email mancante, email non valida).</p>
        <p>Puoi modificare qualsiasi cella cliccandoci sopra, eliminare righe o aggiungerne di nuove prima di confermare l'import.</p>
      </div>
    </div>
  );
}

export default ImportPreviewTable;
